import { useCallback } from 'react'

import { NavigateOptions } from 'react-router-dom'

import logger from '@cash-web/shared/util-logger'
import { useJourneyContext } from '@cash-web/shared-journey-data-access-context'
import { JourneyScope } from '@cash-web/shared-journey-util-main'

import { isClientRoute } from './isClientRoute'
import { transformReceiptUrl } from './transformReceiptUrl'

// This literal is required to reject script URLs before they reach window.open.
// eslint-disable-next-line no-script-url
const UNSAFE_URL_PROTOCOLS = new Set(['javascript:', 'data:', 'vbscript:', 'file:'])

export type LoadUrlOptions = NavigateOptions & { scope?: Partial<JourneyScope> }
export type LoadUrlProps = { trackingName?: string; url?: string; options?: LoadUrlOptions; target?: string }

export type LoadUrlLocationState = Partial<{
  clientRouteReferrerPath: string
  payLinkToken: string
}>

export const useSafeHook =
  <T extends (...args: unknown[]) => unknown>(hook: T) =>
  (...args: Parameters<T>) => {
    try {
      return hook(...args) as ReturnType<T>
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      return undefined
    }
  }

export const useLoadUrl = () => {
  const journey = useJourneyContext()

  const loadUrl = useCallback(
    async ({ url, options, target = '_blank' }: LoadUrlProps) => {
      try {
        if (!url) {
          return
        }

        const parsedUrl = new URL(url)
        if (UNSAFE_URL_PROTOCOLS.has(parsedUrl.protocol.toLowerCase())) {
          return
        }

        const decodedPathname = decodeURIComponent(parsedUrl.pathname)
        const pathnameWithSearch = decodedPathname + parsedUrl.search

        if (isClientRoute(url) && decodedPathname) {
          await journey.navigate(pathnameWithSearch, options)
        } else if (target === '_blank') {
          window.open(transformReceiptUrl(url), target, 'noopener,noreferrer')
        } else {
          window.open(transformReceiptUrl(url), target)
        }
      } catch (error) {
        logger.error(new Error(`Error in loadUrl: ${error instanceof Error ? error.message : error}`))
      }
    },
    [journey]
  )
  return loadUrl
}
